/* eslint-disable jira/naming/disallow-filenames */
/** @jsx jsx */
// eslint-disable-next-line jira/filename-case
import React, { useCallback } from 'react';
import { css, jsx } from '@compiled/react';
import Form from '@atlaskit/form/form';
import { FormFooter } from '@atlaskit/form/form-footer';
import { FormSection } from '@atlaskit/form/form-section';
import Box from '@atlaskit/primitives/box';
import Flex from '@atlaskit/primitives/components/flex';
import Stack from '@atlaskit/primitives/stack';
import { xcss } from '@atlaskit/primitives/xcss';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { JiraBoardType } from './__generated__/useBoardCreateMutationUseBoardCreateMutation.graphql.ts';
import type { BoardCreateFormValues, ModalClosehandler } from './BoardCreateFormValues.types.tsx';
import { useBoardCreateController } from './useBoardCreateController.tsx';
import { ModalHeader } from './ModalHeader.tsx';
import { ModalIllustration } from './ModalIllustration.tsx';
import { FormActions } from './FormActions.tsx';
import { BoardNameField } from './BoardNameField.tsx';
import { SourceTypePicker } from './SourceTypePicker.tsx';
import { SourceValuePicker, SOURCE_VALUE_FIELD_NAME } from './SourceValuePicker.tsx';
import { validateFormData } from './validateFormData.tsx';

export type BoardCreateFormProps = {
	boardType: JiraBoardType;
	projectId: string;
	onClose: ModalClosehandler;
};

export const BoardCreateFormFallback = (): JSX.Element => {
	return (
		<Flex xcss={formContainerStyles}>
			<Flex gap="space.1000" xcss={formContentStyles}>
				<Stack xcss={columnStyles} spread="space-between">
					<Stack space="space.300">
						<Stack space="space.150">
							<Skeleton width="150px" height="20px" />
							<Skeleton width="100%" height="12px" />
							<Skeleton width="20%" height="12px" />
						</Stack>
						<Stack space="space.100">
							<Skeleton width="100px" height="12px" />
							<Skeleton width="100%" height="30px" />
						</Stack>
						<Stack space="space.100">
							<Skeleton width="200px" height="12px" />
							<Skeleton width="100%" height="30px" />
						</Stack>
					</Stack>
					<Flex justifyContent="end">
						<Flex gap="space.050">
							<Skeleton width="70px" height="32px" />
							<Skeleton width="70px" height="32px" />
						</Flex>
					</Flex>
				</Stack>
				<Stack xcss={columnStyles} space="space.200">
					<Skeleton width="150px" height="20px" />
					<Skeleton width="412px" height="307px" borderRadius={token('radius.xxlarge')} />
				</Stack>
			</Flex>
		</Flex>
	);
};

export const BoardCreateForm = ({
	boardType,
	projectId,
	onClose,
}: BoardCreateFormProps): JSX.Element => {
	const { submit } = useBoardCreateController();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleOnSubmit = useCallback(
		async (data: BoardCreateFormValues) => {
			const errors = validateFormData(data);
			const hasValidationErrors = errors.boardName || errors.sourceType || errors.sourceValue;

			let sourceValueLength = 0;
			if (data.sourceValue != null) {
				sourceValueLength = Array.isArray(data.sourceValue) ? data.sourceValue.length : 1;
			}

			const event = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			});
			fireUIAnalytics(event, 'createNewBoard', {
				sourceType: data.sourceType,
				sourceValueLength,
				errors: hasValidationErrors && errors,
			});

			if (hasValidationErrors) {
				return errors;
			}

			await submit({
				boardType,
				formValues: data,
				locationProjectId: projectId ?? '',
				onSuccess: () => onClose(undefined, undefined, true),
			});
		},
		[createAnalyticsEvent, submit, boardType, projectId, onClose],
	);

	const resetSourceValue = useCallback(
		(
			setFieldValue: (
				name: string,
				value: BoardCreateFormValues['sourceType'] | null | undefined,
			) => void,
		) =>
			() =>
				setFieldValue(SOURCE_VALUE_FIELD_NAME, undefined),
		[],
	);

	return (
		<Flex xcss={formContainerStyles}>
			<Form onSubmit={handleOnSubmit}>
				{({ formProps, submitting, setFieldValue }) => (
					<form {...formProps} css={formStyles} noValidate>
						<Flex gap="space.1000" xcss={formContentStyles}>
							<Stack xcss={columnStyles}>
								<ModalHeader type={boardType} />
								<Box xcss={formFieldsContainerStyles}>
									<FormSection>
										<BoardNameField />
									</FormSection>
									<FormSection>
										<SourceTypePicker resetSourceValue={resetSourceValue(setFieldValue)} />
										<SourceValuePicker />
									</FormSection>
								</Box>
								<Box xcss={buttonGroupContainerStyles}>
									<FormFooter>
										<FormActions submitting={submitting} onClose={onClose} />
									</FormFooter>
								</Box>
							</Stack>
							<Stack xcss={columnStyles}>
								<ModalIllustration />
							</Stack>
						</Flex>
					</form>
				)}
			</Form>
		</Flex>
	);
};

const formStyles = css({
	overflowY: 'auto',
	width: '100%',
});

const formContentStyles = xcss({
	height: '100%',
	width: '100%',
});

const columnStyles = xcss({
	width: 'calc(50% - 40px)', // space.1000 is 80px, thus 40px is the space on each side
	flex: 'none',
	paddingTop: 'space.200',
});

const formFieldsContainerStyles = xcss({
	flexGrow: 1,
});

const buttonGroupContainerStyles = xcss({
	alignSelf: 'end',
	justifySelf: 'end',
});

const formContainerStyles = xcss({
	paddingTop: 'space.600',
	paddingLeft: 'space.600',
	paddingRight: 'space.600',
	paddingBottom: 'space.500',
	height: '100%',
});
